import {
  Headings,
  Button,
  Image,
  Label,
  Paragraph,
  Loader,
  ErrorMessage,
} from '@sentara-health/optimahealth_web';
import {
  LabServicePopUp,
  PrimaryCarePopUp,
  NurseOnCallPopUp,
  UrgentCarePopUp,
  BehavioralPopUp,
  MyCareCoordinatorPopUp,
  CareTile,
} from 'templates';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  navigateToExternalUrl,
  isUnAuthorizedError,
  unAuthorizedErrorHandler,
  getDecryptedValue,
  UserDetails,
  NavigateConst,
  ANALYTICS_MESSAGES,
  generateCustomAdobeContentTitle,
  useAnalytics,
} from 'common';
import { useNavigate } from 'react-router-dom';
import {
  useMemberEligibility,
  useVirtualVisitCategory,
  VirtualCareCategoryResponse,
} from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from 'context';

interface EligibilityItems {
  type?: CodeType;
  value?: string;
  [key: string]: any;
}
interface Coding {
  code?: string;
  display?: string;
  [key: string]: any;
}
interface CodeType {
  coding?: Coding;
  [key: string]: any;
}

function VirtualVisitCare() {
  const { t } = useTranslation();

  const effectRan = useRef(false);
  const loadErrorTracked = useRef(false);
  const { trackContentListing } = useAnalytics();
  const videoIframeRef = useRef<HTMLIFrameElement>(null);
  const videoClickTracked = useRef(false);
  const videoPlayerUrl = t('virtualVisitCare.virtualCareVideoLink');

  const [loading, setLoading] = useState<boolean>(false);

  const [errorMessage, showErrorMessage] = useState<boolean>(false);

  const [virtualVisitDashboard, setVirtualVisitDashboard] =
    useState<VirtualCareCategoryResponse>();

  const [memberEligibilityBenefitsList, setMemberEligibilityBenefitsList] =
    useState<EligibilityItems[]>();

  const { MemberEligibilityRequest } = useMemberEligibility();
  const { VirtualVisitCategoryRequest } = useVirtualVisitCategory();
  const { loginMemberId, getFeature, ssoEndPoints } = useContext(GlobalContext);

  const isSentaraEAPEnabled = getFeature?.SentaraEAP ?? false;
  const isMedicaidOrDsnpMember =
    !!getFeature?.HomeAddressMedicaid || !!getFeature?.HomeAddressDSNP;

  const [sentaraBenefitObject, setSentaraBenefitObject] = useState<
    Record<string, any>
  >({});
  const [mdliveBenefitObject, setMdLiveBenefitObject] = useState<
    Record<string, any>
  >({});

  useEffect(() => {
    const handleWindowBlur = () => {
      if (
        document.activeElement === videoIframeRef.current &&
        !videoClickTracked.current
      ) {
        videoClickTracked.current = true;
        trackContentListing(
          '818',
          ANALYTICS_MESSAGES.VIRTUAL_VISIT_VIDEO_CLICK,
          7
        );
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [trackContentListing]);

  useEffect(() => {
    if (effectRan.current) return;
    if (loginMemberId !== null && loginMemberId !== '') {
      fetchToken();
      effectRan.current = true;
    }
  }, [loginMemberId]);

  const fetchToken = async () => {
    setLoading(true);
    const bearerToken = await getDecryptedValue(UserDetails.bearerToken);
    if (loginMemberId !== null && loginMemberId !== '') {
      const EligibilityrequestBody = {
        identifier: [
          {
            system: 'memberId',
            value: loginMemberId,
          },
        ],
      };
      const requestBody = {
        memberId: loginMemberId,
      };
      const eligibilityResponse = await MemberEligibilityRequest(
        bearerToken,
        EligibilityrequestBody
      );
      if (
        eligibilityResponse?.data != null &&
        Array.isArray(eligibilityResponse?.data?.eligibilityPlans) &&
        eligibilityResponse?.data?.eligibilityPlans.length > 0
      ) {
        setMemberEligibilityBenefitsList(
          eligibilityResponse?.data?.eligibilityPlans[0]?.items
        );
      } else {
        const errorCode = eligibilityResponse?.error ?? '';
          if (isUnAuthorizedError(errorCode)) {
            unAuthorizedErrorHandler();
          }
      }
      fetchVirtualCare(bearerToken, requestBody);
    } else {
      setLoading(false);
    }
  };

  const fetchVirtualCare = async (
    accessToken: string,
    requestBody: { memberId: any }
  ) => {
    const response = await VirtualVisitCategoryRequest(
      accessToken,
      requestBody
    );
    if (response?.data != null) {
      const normalizedData: VirtualCareCategoryResponse =
        normalizeVirtualVisitDashboard(response.data, isMedicaidOrDsnpMember);
      setVirtualVisitDashboard(normalizedData);
    } else if (response?.errorCode) {
      handleError(response.errorCode);
    }
    setLoading(false);
  };

  const handleError = (errorCode: string) => {
    setLoading(false);
    if (isUnAuthorizedError(errorCode)) {
      unAuthorizedErrorHandler();
    } else {
      // Both API calls can fail; report the page load error only once.
      if (!loadErrorTracked.current) {
        loadErrorTracked.current = true;
        trackContentListing(
          '806',
          ANALYTICS_MESSAGES.VIRTUAL_VISIT_PAGE_LOAD_ERROR,
          6
        );
      }
      showErrorMessage(true);
    }
  };

  useEffect(() => {
    if (!memberEligibilityBenefitsList || !virtualVisitDashboard?.result)
      return;
    const copayObject = Object.fromEntries(
      memberEligibilityBenefitsList.map(({ type, value }) => [
        type?.coding?.code,
        value,
      ])
    );
    const benefitObjects: Record<string, Record<string, any>> = {
      mdliveCoPay: {},
      mdliveCoPayInsurance: {},
      sentaraCoPayIndex: {},
      sentaraCoPayInsuranceIndex: {},
    };
    virtualVisitDashboard.result.forEach(({ description, ...rest }: any) => {
      Object.entries(benefitObjects).forEach(([key, target]) => {
        if (rest[key] && copayObject[rest[key]]) {
          target[
            `${description}${key === 'mdliveCoPayInsurance' ||
              key === 'sentaraCoPayInsuranceIndex'
              ? ' Insurance'
              : ''
            }`
          ] = copayObject[rest[key]];
        }
      });
    });
    setSentaraBenefitObject({
      ...benefitObjects.sentaraCoPayIndex,
      ...benefitObjects.sentaraCoPayInsuranceIndex,
    });
    setMdLiveBenefitObject({
      ...benefitObjects.mdliveCoPay,
      ...benefitObjects.mdliveCoPayInsurance,
    });
  }, [memberEligibilityBenefitsList, virtualVisitDashboard]);

  type ModalState = {
    isLabOpen: boolean;
    isNurseOpen: boolean;
    isPrimaryCareOpen: boolean;
    isUrgentCareOpen: boolean;
    isBehavioralHealthOpen: boolean;
    isMyCareCoordinatorOpen: boolean;
  };

  const [modalStates, setModalStates] = useState<ModalState>({
    isLabOpen: false,
    isNurseOpen: false,
    isPrimaryCareOpen: false,
    isUrgentCareOpen: false,
    isBehavioralHealthOpen: false,
    isMyCareCoordinatorOpen: false,
  });

  const setModalState = (modalName: keyof ModalState, value: boolean) => {
    setModalStates((prevState) => ({
      ...prevState,
      [modalName]: value,
    }));
  };

  const handleEAP = () => {
    navigateToExternalUrl(
      getFeature?.VirtualVisitEAP
        ? ssoEndPoints?.VIRGINIA_BEACH_URL
        : ssoEndPoints?.SENTARA_URL
    );
  };
  const handleModalOpen = (modalName: keyof ModalState) => {
    setModalState(modalName, true);
  };

  const onScheduleLabVisitClick = () => handleModalOpen('isLabOpen');
  const onScheduleNurseVisitClick = () => handleModalOpen('isNurseOpen');
  const onPrimaryCareClick = () => handleModalOpen('isPrimaryCareOpen');
  const onUrgentCareClick = () => handleModalOpen('isUrgentCareOpen');
  const onBehavioralHealthClick = () =>
    handleModalOpen('isBehavioralHealthOpen');
  const onMyCareCoordinatorClick = () => handleModalOpen('isMyCareCoordinatorOpen');
  const handleNavigation = (url: string) => () => navigate(url);

  const onScheduleVirtualVisitClick = () => {
    trackContentListing(
      '803',
      ANALYTICS_MESSAGES.VIRTUAL_VISIT_SCHEDULE_CLICK,
      3
    );
    onUrgentCareClick();
  };

  const onLearnMoreClick = () => {
    trackContentListing(
      '804',
      ANALYTICS_MESSAGES.VIRTUAL_VISIT_LEARN_MORE_CLICK,
      4
    );
    handleEAP();
  };

  const navigate = useNavigate();

  const popupAnalyticsNames: Record<keyof ModalState, string> = {
    isLabOpen: 'Lab',
    isNurseOpen: '24/7 Nurse Advice Line',
    isPrimaryCareOpen: 'Primary Care and Chronic Care',
    isUrgentCareOpen: 'Urgent Care',
    isBehavioralHealthOpen: 'Mental Health and Behavioral Therapy',
    isMyCareCoordinatorOpen: 'My Care Coordinator',
  };

  function closeVirtualVisitModal() {
    const openModal = (Object.keys(modalStates) as (keyof ModalState)[]).find(
      (key) => modalStates[key]
    );
    if (openModal) {
      trackContentListing(
        '805',
        generateCustomAdobeContentTitle(
          popupAnalyticsNames[openModal],
          ANALYTICS_MESSAGES.VIRTUAL_VISIT_POPUP_CLOSE_SUFFIX
        ),
        5
      );
    }
    setModalStates({
      isLabOpen: false,
      isNurseOpen: false,
      isPrimaryCareOpen: false,
      isUrgentCareOpen: false,
      isBehavioralHealthOpen: false,
      isMyCareCoordinatorOpen: false,
    });
  }

  enum VirtualTiles {
    MENTAL_HEALTH_BEHAVIOR_THERAPY = 'Mental Health and Behavioral Therapy',
    PHARMACY_TOOLS = 'Pharmacy',
    URGENTCARE = 'Urgent Care',
    NURSE_ON_CALL = '24/7 Nurse Advice Line',
    EMPLOYEE_ASSISTANCE_PROGRAM = 'Employee Assistance Program',
    PRIMARY_CARE_CHRONIC_CONDITION = 'Primary Care and Chronic Care',
    LAB = 'Lab',
    PHYSICAL_THERAPY = 'Physical Therapy',
    MINOR_ILLNESSES = 'Minor Illnesses and Conditions',
    MY_CARE_COORDINATOR = 'My Care Coordinator',
  }

  const tileTranslationKeys: Record<string, string> = {
    [VirtualTiles.MENTAL_HEALTH_BEHAVIOR_THERAPY]: 'mentalHealth',
    [VirtualTiles.PHARMACY_TOOLS]: 'pharmacy',
    [VirtualTiles.URGENTCARE]: 'urgentCare',
    [VirtualTiles.NURSE_ON_CALL]: 'nurseOnCall',
    [VirtualTiles.EMPLOYEE_ASSISTANCE_PROGRAM]: 'employeeAssistance',
    [VirtualTiles.PRIMARY_CARE_CHRONIC_CONDITION]: 'primaryCare',
    [VirtualTiles.LAB]: 'lab',
    [VirtualTiles.PHYSICAL_THERAPY]: 'physicalTherapy',
    [VirtualTiles.MINOR_ILLNESSES]: 'minorIllnesses',
    [VirtualTiles.MY_CARE_COORDINATOR]: 'myCareCoordinator',
  };

  const popups = [
    { isOpen: modalStates.isLabOpen, Component: LabServicePopUp },
    {
      isOpen: modalStates.isPrimaryCareOpen,
      Component: PrimaryCarePopUp,
      mdliveCopay: copayAmount(
        mdliveBenefitObject,
        VirtualTiles.PRIMARY_CARE_CHRONIC_CONDITION,
        t
      ),
      sentaraCopay: copayAmount(
        sentaraBenefitObject,
        VirtualTiles.PRIMARY_CARE_CHRONIC_CONDITION,
        t
      ),
    },
    { isOpen: modalStates.isNurseOpen, Component: NurseOnCallPopUp },
    { isOpen: modalStates.isMyCareCoordinatorOpen, Component: MyCareCoordinatorPopUp },
  
    {
      isOpen: modalStates.isUrgentCareOpen,
      Component: UrgentCarePopUp,
      mdliveCopay: copayAmount(
        mdliveBenefitObject,
        VirtualTiles.MINOR_ILLNESSES,
        t,
        isMedicaidOrDsnpMember
      ),
      sentaraCopay: copayAmount(
        sentaraBenefitObject,
        VirtualTiles.MINOR_ILLNESSES,
        t,
        isMedicaidOrDsnpMember
      ),
    },
    {
      isOpen: modalStates.isBehavioralHealthOpen,
      Component: BehavioralPopUp,
      mdliveCopay: copayAmount(
        mdliveBenefitObject,
        VirtualTiles.MENTAL_HEALTH_BEHAVIOR_THERAPY,
        t,
        isMedicaidOrDsnpMember
      ),
      sentaraCopay: copayAmount(
        sentaraBenefitObject,
        VirtualTiles.MENTAL_HEALTH_BEHAVIOR_THERAPY,
        t,
        isMedicaidOrDsnpMember
      ),
    },
  ];

  const tileActions: Record<VirtualTiles, () => void> = {
    [VirtualTiles.MENTAL_HEALTH_BEHAVIOR_THERAPY]: onBehavioralHealthClick,
    [VirtualTiles.PHARMACY_TOOLS]: handleNavigation(
      NavigateConst.pharmacyTools
    ),
    [VirtualTiles.URGENTCARE]: onUrgentCareClick,
    [VirtualTiles.NURSE_ON_CALL]: onScheduleNurseVisitClick,
    [VirtualTiles.EMPLOYEE_ASSISTANCE_PROGRAM]: handleEAP,
    [VirtualTiles.PRIMARY_CARE_CHRONIC_CONDITION]: onPrimaryCareClick,
    [VirtualTiles.LAB]: onScheduleLabVisitClick,
    [VirtualTiles.PHYSICAL_THERAPY]: handleNavigation(
      NavigateConst.physicalTherapy
    ),
    [VirtualTiles.MINOR_ILLNESSES]: onUrgentCareClick,
    [VirtualTiles.MY_CARE_COORDINATOR]: onMyCareCoordinatorClick,
  };

  const clickTile = (title: string, index: number) => {
    trackContentListing(
      '802',
      generateCustomAdobeContentTitle(
        title,
        ANALYTICS_MESSAGES.VIRTUAL_VISIT_TILE_CLICK_SUFFIX
      ),
      index + 1
    );
    if (title in tileActions) {
      const action = tileActions[title as VirtualTiles];
      action();
    }
  };

  const MEDICAID_CARE_COORDINATOR_TILE = {
    resourceType: 'VirtualCareCategory',
    name: 'My Care Coordinator',
    rank: 4,
    fontAwesomeIconKey: 'handshake',
    active: true,
    description: '',
    sentaraCoPayIndex: '',
    mdliveCoPay: '',
    telecom: [],
  };

  const normalizeVirtualVisitDashboard = (
    data: VirtualCareCategoryResponse,
    isMedicaidOrDsnpMember: boolean
  ) => {
    if (!isMedicaidOrDsnpMember || !Array.isArray(data?.result)) {
      return data;
    }

    return {
      ...data,
      result: data.result.map((item: any) =>
        item?.name === 'Employee Assistance Program'
          ? {
              ...item,
              ...MEDICAID_CARE_COORDINATOR_TILE,
            }
          : item
      ),
    } as VirtualCareCategoryResponse;
  };

  const onBackClick = () => {
    trackContentListing('801', ANALYTICS_MESSAGES.VIRTUAL_VISIT_BACK_CLICK, 1);
    navigate(NavigateConst.dashboard);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onBackClick();
    }
  };

  return (
    <>
      {loading && (
        <div>
          <Loader />
        </div>
      )}

      {/* To show the internalServerError message */}
      {errorMessage && <ErrorMessage>{t('common.internalServerError')}</ErrorMessage>}

      <div>
        <div
          data-testid="back"
          className="d-flex flex-row align-items-center cursor-pointer"
          onClick={onBackClick}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <span className="arrow-back-black" />
          <Headings
            level={1}
            text={t('common.virtualVisit')}
            variant="semiBold"
            data-testid={'getCareNowHeader'}
          />
        </div>
        <div className="mt-3 d-flex flex-column fs-5">
          <Label dataTestId={'getCareServices'}>
            {t('virtualVisitCare.getCareServices')}
          </Label>
          <Label className="mb-3 font-size-14" dataTestId={'getCareDisclaimer'}>
            {t('virtualVisitCare.getCareDisclaimer')}
          </Label>
        </div>

        <div className="grid-cont d-flex">
          <div
            className="left-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
          {virtualVisitDashboard?.result
            .filter((item) =>
              !(item.name === VirtualTiles.EMPLOYEE_ASSISTANCE_PROGRAM && !isSentaraEAPEnabled)
            )
            .map((item, index) => {
              const translationKey = tileTranslationKeys[item.name ?? ''];
              return (
                <CareTile
                  key={item.name ?? ''}
                  stackIconOnMobile={false}
                  heading={translationKey ? t('virtualVisitCare.' + translationKey) : item.name ?? ''}
                  fontAwesomeIconKey={item.fontAwesomeIconKey ?? ''}
                  onClick={() => clickTile(item.name ?? '', index)}
                />
              );
            })}
          <div
            className="bottom-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
        </div>

        <div className="my-4">
          <Headings
            level={1}
            text={t('virtualVisitCare.title')}
            variant="semiBold"
            data-testid="whatWeOfferHeader"
          />
        </div>

        <div className="d-flex flex-column my-2">
          <div className="row mb-2 ">
            <div className="mb-3 col-md-6 order-md-first mt-2 mt-md-0">
              <Headings
                level={3}
                text={t('virtualVisitCare.virtualVisit')}
                variant={'semiBold'}
              />
              <div className="mt-4">
                <Paragraph
                  text={t('virtualVisitCare.whatWeOfferContent')}
                  variant="regular"
                  data-testid={'whatWeOfferContent'}
                  className="mt-2"
                />
                <Paragraph
                  level={2}
                  text={t('virtualVisitCare.disclaimerContent')}
                  variant="regular"
                  data-testid={'disclaimerContent'}
                  className="mb-4"
                />
                <Button
                  data-testid={'scheduleVirtualVisit'}
                  variant="primary_btn"
                  onClick={onScheduleVirtualVisitClick}
                >
                  {t('virtualVisitCare.scheduleVirtualVisit')}
                </Button>
                {popups.map(
                  ({ isOpen, Component, mdliveCopay, sentaraCopay }) =>
                    isOpen && (
                      <Component
                        key={Component.name}
                        closeModal={closeVirtualVisitModal}
                        mdliveCopay={mdliveCopay}
                        sentaraCopay={sentaraCopay}
                      />
                    )
                )}
              </div>
            </div>
            <div className="col-md-6 order-first">
              <iframe
                ref={videoIframeRef}
                className="rounded-3 mt-4"
                id="video"
                width="100%"
                height="225"
                allowFullScreen
                src={videoPlayerUrl}
                data-testid="video-iframe"
                title="Virtual Visit Video"
              />
            </div>
          </div>

          {isSentaraEAPEnabled ? (
            <div className="row my-4 py-4">
              <div className="mb-4 col-md-6">
                <Image
                  data-testid={'virtualVisitLogo'}
                  className="w-100 h-55 rounded-4"
                  path={'/virtualvisitlogo.png'}
                />
              </div>
              <div className="mb-3 col-md-6">
                <Headings
                  level={3}
                  text={t('virtualVisitCare.additionalBenefitsTitle')}
                  variant={'semiBold'}
                />
                <div className="mt-4">
                  <Paragraph
                    text={t('virtualVisitCare.additionalBenefitsContent')}
                    variant="regular"
                    data-testid={'additionalBenefitsContent'}
                  />
                  <div className="mt-4">
                    <Button
                      data-testid={'Learn More'}
                      variant="primary_btn"
                      onClick={onLearnMoreClick}
                    >
                      {t('pharmacyTools.learnMore')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}



export const copayAmount = (
  benefitObject: Record<string, any>,
  titleKey: string,
  t: (translationKey: string) => string,
  isMedicaidOrDsnpMember: boolean = false
): string => {
  const NO_CHARGE = t('benefits.noCharge').replace('No Charge', 'No charge');
  const NOT_AVAILABLE = t('benefits.notAvailable');
  if (isMedicaidOrDsnpMember) {
    return NO_CHARGE;
  }

  let copay = benefitObject[titleKey]?.toString();
  let insuranceCopay = benefitObject[`${titleKey} Insurance`]?.toString();
  const COPAY_MESSAGE = (amount: string) => `$${amount} ${t('benefits.copay')}`;
  const COINSURANCE_MESSAGE = (percentage: string) =>
    `${percentage}% ${t('benefits.coinsurance')}`;
  if (copay === 'N/A') {
    copay = '';
  }

  if (insuranceCopay === 'N/A') {
    insuranceCopay = '';
  }

  if (copay && copay !== '0') {
    return COPAY_MESSAGE(copay);
  } else if (copay && copay === '0') {
    return NO_CHARGE;
  }

  if (insuranceCopay && insuranceCopay !== '0') {
    return COINSURANCE_MESSAGE(insuranceCopay);
  } else if (insuranceCopay && insuranceCopay === '0') {
    return NO_CHARGE;
  }

  return NOT_AVAILABLE;
};

export default VirtualVisitCare;